// Pure route-optimization helpers for "Today's Route".
//
// No React, no DOM, no network — just math. This is the single place where the
// distance/time model lives, so a Google road-distance/ETA matrix can later be
// swapped in for the haversine straight-line estimate without touching the UI.
//
// Coordinate shape used everywhere here: { lat, lng } (numbers, degrees).

const EARTH_RADIUS_MILES = 3958.8;
const DEFAULT_MPH = 30; // city/suburban driving estimate
// Google Maps directions supports ~25 points total (origin + dest + 23 waypoints).
const MAX_WAYPOINTS = 23;

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Great-circle (straight-line) distance between two {lat,lng} points, in miles.
 * Earth radius 3958.8 mi. This is an ESTIMATE — real road distance is longer.
 */
export function haversineMiles(a, b) {
  if (!a || !b) return 0;
  const lat1 = Number(a.lat);
  const lng1 = Number(a.lng);
  const lat2 = Number(b.lat);
  const lng2 = Number(b.lng);
  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lng1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lng2)
  ) {
    return 0;
  }
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinLng * sinLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_MILES * c;
}

/**
 * Estimated drive minutes for a straight-line distance.
 * @param {number} miles
 * @param {number} [mph=30]
 * @returns {number} whole minutes
 */
export function estimateMinutes(miles, mph = DEFAULT_MPH) {
  const m = Number(miles);
  const speed = Number(mph);
  if (!Number.isFinite(m) || m <= 0) return 0;
  if (!Number.isFinite(speed) || speed <= 0) return 0;
  return Math.round((m / speed) * 60);
}


// Greedy nearest-neighbor seeded at home. Deterministic: ties broken by the
// stop's original index so the same input always yields the same order.
// `distFn(a,b)` supplies the distance model (straight-line by default; a real
// road-distance matrix can be injected via optimizeRoundTrip).
function nearestNeighbor(home, stops, distFn) {
  const remaining = stops.map((s, idx) => ({ s, idx }));
  const order = [];
  let current = home;
  while (remaining.length) {
    let bestPos = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = distFn(current, remaining[i].s);
      if (
        d < bestDist ||
        (d === bestDist && remaining[i].idx < remaining[bestPos].idx)
      ) {
        bestDist = d;
        bestPos = i;
      }
    }
    const [picked] = remaining.splice(bestPos, 1);
    order.push(picked.s);
    current = picked.s;
  }
  return order;
}

// 2-opt local search over the round trip (home is a fixed endpoint on both
// ends). Reverses segments while that shortens the tour; stops when a full pass
// yields no improvement or the iteration cap is hit. Deterministic.
function twoOpt(home, seq, distFn) {
  const route = seq.slice();
  const n = route.length;
  if (n < 3) return route;

  const maxIterations = 50;
  let improved = true;
  let iterations = 0;

  while (improved && iterations < maxIterations) {
    improved = false;
    iterations++;
    for (let i = 0; i < n - 1; i++) {
      for (let k = i + 1; k < n; k++) {
        // Endpoints of the two edges being considered for a swap.
        const a = i === 0 ? home : route[i - 1];
        const b = route[i];
        const c = route[k];
        const d = k === n - 1 ? home : route[k + 1];

        const before = distFn(a, b) + distFn(c, d);
        const after = distFn(a, c) + distFn(b, d);

        // Strict improvement only (epsilon guards float noise) => terminates.
        if (after + 1e-9 < before) {
          let lo = i;
          let hi = k;
          while (lo < hi) {
            const tmp = route[lo];
            route[lo] = route[hi];
            route[hi] = tmp;
            lo++;
            hi--;
          }
          improved = true;
        }
      }
    }
  }
  return route;
}

/**
 * Optimize a ROUND TRIP starting and ending at `home`.
 *
 * @param {{lat:number,lng:number}} home
 * @param {{technician_id:string,route_date:string}|null} persist  when set, the order is also
 *        written to job_headers.route_seq so the field app picks it up right away.  Tech's home (start + end).
 * @param {Array<{id:*,lat:number,lng:number}>} stops  Stops with VALID numeric
 *        coords (caller filters out un-routable ones). Extra fields pass through.
 * @returns {{
 *   ordered: Array,                                  // stops in visit order
 *   legs: Array<{fromLabel:string,toLabel:string,miles:number}>,
 *   totalMiles: number
 * }}
 *   The tour is home -> ordered[0] -> ... -> ordered[last] -> home.
 *   legs has ordered.length + 1 entries (the final leg returns to home).
 *   Empty/one-stop inputs are handled. Deterministic.
 */
export function optimizeRoundTrip(home, stops, opts = {}) {
  const list = Array.isArray(stops) ? stops.slice() : [];

  if (!home || list.length === 0) {
    return { ordered: [], legs: [], totalMiles: 0, totalMinutes: 0, basis: "straight-line" };
  }

  // If a road matrix is supplied it uses REAL driving distance/time; index 0 = home,
  // index i = stops[i-1] (the SAME order the caller passed to route-matrix). Any
  // missing pair falls back to the straight-line estimate, so a partial matrix is safe.
  const m = (opts.matrix && Array.isArray(opts.matrix.dist)) ? opts.matrix : null;
  const idx = new Map([[home, 0], ...list.map((s, i) => [s, i + 1])]);
  const distFn = m
    ? (a, b) => { const i = idx.get(a), j = idx.get(b); const v = (i != null && j != null) ? m.dist?.[i]?.[j] : null; return (v == null) ? haversineMiles(a, b) : v; }
    : haversineMiles;
  const durFn = (m && Array.isArray(m.dur))
    ? (a, b) => { const i = idx.get(a), j = idx.get(b); const v = (i != null && j != null) ? m.dur?.[i]?.[j] : null; return (v == null) ? estimateMinutes(haversineMiles(a, b)) : v; }
    : (a, b) => estimateMinutes(haversineMiles(a, b));

  let ordered;
  if (list.length === 1) {
    ordered = list;
  } else {
    const seeded = nearestNeighbor(home, list, distFn);
    ordered = twoOpt(home, seeded, distFn);
  }

  const legs = [];
  let totalMiles = 0;
  let totalMinutes = 0;
  let prev = home;
  let prevLabel = "Home";

  for (let i = 0; i < ordered.length; i++) {
    const stop = ordered[i];
    const miles = distFn(prev, stop);
    const minutes = durFn(prev, stop);
    const toLabel = `Stop ${i + 1}`;
    legs.push({ fromLabel: prevLabel, toLabel, miles, minutes });
    totalMiles += miles;
    totalMinutes += minutes;
    prev = stop;
    prevLabel = toLabel;
  }

  // Return-to-home leg.
  const returnMiles = distFn(prev, home);
  const returnMinutes = durFn(prev, home);
  legs.push({ fromLabel: prevLabel, toLabel: "Home", miles: returnMiles, minutes: returnMinutes });
  totalMiles += returnMiles;
  totalMinutes += returnMinutes;

  return { ordered, legs, totalMiles, totalMinutes, basis: m ? "road" : "straight-line" };
}

/**
 * Ask the `optimize-route` edge function to order a tech's stops on REAL ROADS and return
 * the per-leg miles/minutes — ONE Google request instead of an N-squared matrix.
 *
 * PREFER THIS OVER fetchRoadMatrix. ComputeRouteMatrix bills per origin-destination PAIR
 * (a 15-stop route = 256 billable elements EVERY call); ComputeRoutes bills per REQUEST, so
 * a whole 4-tech crew costs ~104 requests/month against a 5,000/month free tier. Google also
 * does the ordering itself on the road network, so a client across a river is correctly a
 * bridge away rather than "2 miles". It optimises for TRAVEL TIME, which measured slightly
 * faster (and slightly longer in distance) than our distance-first 2-opt.
 *
 * Returns the SAME shape as optimizeRoundTrip so callers can swap it in, or null on any
 * failure — callers then fall back to optimizeRoundTrip's free straight-line estimate.
 *
 * @param {{lat:number,lng:number}} home
 * @param {{technician_id:string,route_date:string}|null} persist  when set, the order is also
 *        written to job_headers.route_seq so the field app picks it up right away.
 * @param {Array<{id:*,lat:number,lng:number,region?:string}>} stops  `region` (client state)
 *        only matters past 24 stops, where it keeps a chunk from straddling the river.
 */
export async function fetchOptimizedRoute(home, stops, base, authFetch, persist = null) {
  try {
    const list = Array.isArray(stops) ? stops : [];
    if (!home || list.length === 0) return null;
    const res = await authFetch(`${base}/functions/v1/optimize-route`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        home: { lat: Number(home.lat), lng: Number(home.lng) },
        stops: list.map((s) => ({ id: s.id, lat: Number(s.lat), lng: Number(s.lng), region: s.region ?? null })),
        // `persist` writes the resulting order onto job_headers.route_seq so the TECH'S PHONE
        // shows it immediately. Without it the field app keeps the previous order until the
        // refresh-route-plans cron catches up (up to 10 min).
        // NOTE: deliberately never sends `refresh:true`. The cache is keyed on
        // (home + exact stop set), so a real reassignment is already a miss (one Google call)
        // and an unchanged route is a free cache hit — which is what makes a manual
        // "Re-optimize" button safe to press repeatedly. `refresh:true` would bypass that and
        // buy a fresh paid call on every click.
        ...(persist ? { persist } : {}),
      }),
    });
    if (!res.ok) return null;
    const d = await res.json();
    if (!d || !Array.isArray(d.order) || !Array.isArray(d.legs)) return null;

    // Re-hydrate the caller's own stop objects in Google's visit order.
    const byId = new Map(list.map((s) => [String(s.id), s]));
    const ordered = d.order.map((id) => byId.get(String(id))).filter(Boolean);
    if (ordered.length !== list.length) return null;   // partial answer -> fall back

    const legs = d.legs.map((l, i) => ({
      fromLabel: i === 0 ? "Home" : `Stop ${i}`,
      toLabel:   i === d.legs.length - 1 ? "Home" : `Stop ${i + 1}`,
      miles:     Number(l.miles) || 0,
      minutes:   Number(l.minutes) || 0,
    }));
    return {
      ordered,
      legs,
      totalMiles:   Number(d.totalMiles) || 0,
      totalMinutes: Number(d.totalMinutes) || 0,
      basis: "road",
    };
  } catch (_e) { return null; }
}

// Fetch a real road distance/time matrix for [home, ...stops] from the route-matrix
// edge function. Returns { dist, dur } (miles / minutes, index-aligned to the input)
// or null on any failure / when the server key isn't configured — callers then fall
// back to the straight-line estimate. `authFetch` is (url, opts) => fetch-like.
//
// ⚠️ EXPENSIVE — bills per origin-destination PAIR. Kept for reference/fallback only;
// new code should call fetchOptimizedRoute above. See the cost note there.
export async function fetchRoadMatrix(home, stops, base, authFetch) {
  try {
    const points = [home, ...stops].map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
    if (points.length < 2 || points.some(p => !Number.isFinite(p.lat) || !Number.isFinite(p.lng))) return null;
    const res = await authFetch(`${base}/functions/v1/route-matrix`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ points }),
    });
    if (!res.ok) return null;
    const d = await res.json();
    return (d && Array.isArray(d.dist) && Array.isArray(d.dur)) ? { dist: d.dist, dur: d.dur } : null;
  } catch (_e) { return null; }
}

/**
 * Google Maps directions URL for the ROUND TRIP.
 * origin = home, destination = home, waypoints = ordered stops (in order).
 *
 * @param {{lat:number,lng:number}} home
 * @param {{technician_id:string,route_date:string}|null} persist  when set, the order is also
 *        written to job_headers.route_seq so the field app picks it up right away.
 * @param {Array<{lat:number,lng:number}>} orderedStops
 * @returns {string} https://www.google.com/maps/dir/?api=1&...
 */
export function mapsDirUrl(home, orderedStops) {
  const fmt = (p) => `${Number(p.lat)},${Number(p.lng)}`;
  const homeStr = home ? fmt(home) : "";
  const stops = Array.isArray(orderedStops) ? orderedStops : [];

  const params = new URLSearchParams();
  params.set("api", "1");
  params.set("origin", homeStr);
  params.set("destination", homeStr);

  // Cap waypoints to Google's supported limit; extras are dropped (the ordered
  // list on screen still shows them).
  const wp = stops.slice(0, MAX_WAYPOINTS).map(fmt);
  if (wp.length) {
    // URLSearchParams already percent-encodes the "|" separator.
    params.set("waypoints", wp.join("|"));
  }
  params.set("travelmode", "driving");

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}
